import * as yup from 'yup';
import {
  closedCorpCompanyRegNumRegex,
  privateCompanyRegNumRegex,
  publicCompanyRegNumRegex,
  vatRegex,
  postalCodeRegex,
  maxTradingAsCharacters
} from '../../../constants';

export const businessDetailsValidationSchema = yup.object().shape({
  name: yup
    .string()
    .when('$businessType', ([businessType], schema) =>
      businessType === 'Sole Proprietorship'
        ? schema.trim().min(1, 'Business Name must be at least 1 character long')
        : schema.trim().min(1, 'Business Name must be at least 1 character long').required('Business Name is required')
    ),

  regNumber: yup
    .string()
    .when('$businessType', ([businessType], schema) =>
      businessType !== 'Sole Proprietorship'
        ? schema
          .required('Business Registration Number is required')
          .test('valid-regNumber', 'Invalid Business Registration Number format', (value, context) => {
            if (!value) { return false; }
            const regexMap = {
              'Private Company': privateCompanyRegNumRegex,
              'Close Corporation': closedCorpCompanyRegNumRegex,
              'Public Company': publicCompanyRegNumRegex
            };
            return regexMap[context.options.context.businessType]?.test(value) ?? true;
          })
        : schema
    ),

  vatNumber: yup
    .string()
    .when('$businessType', ([businessType], schema) =>
      businessType === 'Sole Proprietorship'
        ? schema.required('VAT Number is required')
          .matches(/^\d{9,12}$/, 'VAT Number must be between 9-12 digits')
          .matches(vatRegex, 'VAT Number must be a valid numerical value in the format 40xxx56789')
        : schema
          .nullable()
          .optional()
          .test(
            'vat-optional-regex',
            'VAT Number must be between 9-12 digits',
            value => !value || /^\d{9,12}$/.test(value)
          )
          .test(
            'vat-optional-format',
            'VAT Number must be a valid numerical value in the format 40xxx56789',
            value => !value || vatRegex.test(value)
          )
    ),

  differentBusinessName: yup.boolean(),

  tradingAs: yup.string().when('differentBusinessName', {
    is: true,
    then: (schema) =>
      schema
        .required('Business trading name is required')
        .max(
          maxTradingAsCharacters,
          `Business trading name must be ${maxTradingAsCharacters} characters or less`
        ),
    otherwise: (schema) => schema
  }),

  landline: yup
    .string()
    .matches(/^(27|\+27|0)\d{9}$/, 'Enter a valid Business phone number')
    .required('Business phone number is required'),

  website: yup.string()
    .nullable()
    .optional()
    .test(
      'is-valid-website',
      'Enter correct url!',
      value =>
        !value ||
        /^((ftp|http|https):\/\/)?(www\.)?(?!.*(ftp|http|https|www\.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?$/gm.test(value)
    ),

  industry: yup.string()
    .required('Category is required'),

  buildingType: yup.string()
    .required('Building Type is required'),

  flatUnitNumber: yup.string().when('buildingType', {
    is: 'Flat/Apartment',
    then: () => yup.number()
      .typeError('flat/unit/floor must be a number')
      .positive('flat/unit/floor must be positive')
      .integer('flat/unit/floor must be a whole number')
      .nullable(),
    otherwise: () => yup.string().nullable().optional()
  }),
  townUnitNumber: yup.string().when('buildingType',
    {
      is: 'Townhouse/Security Estate',
      then: () => yup.number()
        .typeError('unit or house number must be a number')
        .positive('unit or house number must be positive')
        .integer('unit or house number must be a whole number')
        .nullable(),
      otherwise: () => yup.string().nullable().optional()
    }
  ),
  officeUnitNumber: yup.string().when('buildingType',
    {
      is: 'Office',
      then: () => yup.number()
        .typeError('unit or suite number must be a number')
        .positive('unit or suite number must be positive')
        .integer('unit or suite number must be a whole number')
        .nullable(),
      otherwise: () => yup.string().nullable().optional()
    }
  ),
  houseUnitNumber: yup.string().when('buildingType',
    {
      is: 'House',
      then: () => yup.number()
        .typeError('unit or house number must be a number')
        .positive('unit or house number must be positive')
        .integer('unit or house number must be a whole number')
        .nullable(),
      otherwise: () => yup.string().nullable().optional()
    }
  ),

  flatBuilding: yup.string().when('buildingType', {
    is: 'Flat/Apartment',
    then: () => yup.string()
      .required('building name is required'),
    otherwise: () => yup.string().nullable().optional()
  }),
  townBuilding: yup.string().when('buildingType',
    {
      is: 'Townhouse/Security Estate',
      then: () => yup.string()
        .required('estate name is required'),
      otherwise: () => yup.string().nullable().optional()
    }
  ),
  officeBuilding: yup.string().when('buildingType',
    {
      is: 'Office',
      then: () => yup.string()
        .required('building or office park name is required'),
      otherwise: () => yup.string().nullable().optional()
    }
  ),

  houseStreet: yup.string().when('buildingType', {
    is: 'House',
    then: () => yup.string()
      .required('street name is required'),
    otherwise: () => yup.string().nullable().optional()
  }),
  flatStreet: yup.string().when('buildingType', {
    is: 'Flat/Apartment',
    then: () => yup.string()
      .required('Address is required'),
    otherwise: () => yup.string().nullable().optional()
  }),
  townStreet: yup.string().when('buildingType',
    {
      is: 'Townhouse/Security Estate',
      then: () => yup.string()
        .required('street name is required'),
      otherwise: () => yup.string().nullable().optional()
    }
  ),
  officeStreet: yup.string().when('buildingType',
    {
      is: 'Office',
      then: () => yup.string()
        .required('Address is required'),
      otherwise: () => yup.string().nullable().optional()
    }
  ),

  city: yup.string()
    .required('City is required'),

  suburb: yup.string()
    .required('Suburb is required'),

  postalCode: yup.string()
    .matches(postalCodeRegex, 'Postal Code must be 4 digits')
    .required('Postal Code is required'),

  province: yup.string()
    .required('Province is required'),

  isPersonalAddress: yup.boolean().default(false)

  // sharedDirectors: yup
  //   .string()
  //   .when(['$businessType', '$formattedList'], {
  //     is: (businessType, formattedList) =>
  //       businessType !== 'Sole Proprietorship' &&
  //     Array.isArray(formattedList) &&
  //     formattedList.length > 0,
  //     then: (schema) => schema.required('This is a required field'),
  //     otherwise: (schema) => schema.notRequired()
  //   }),

  // sharedBusinessDirectors: yup
  //   .string()
  //   .when(['sharedDirectors', '$businessType', '$formattedList'], {
  //     is: (sharedDirectors, businessType, formattedList) =>
  //       sharedDirectors === 'Yes' &&
  //     businessType !== 'Sole Proprietorship' &&
  //     formattedList.length > 0,
  //     then: (schema) => schema.required('This is a required field'),
  //     otherwise: (schema) => schema.notRequired()
  //   })
});